import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession, useUser, useDescope } from "@descope/react-sdk";

import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  alpha,
  InputBase,
  styled,
  Avatar,
  Tooltip,
} from "@mui/material";
import { Menu, Logout, Search as SearchIcon, HealthAndSafetyOutlined } from "@mui/icons-material";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { setSearchValue } from "../../redux/features/active-steps/active-slice";
import {
  setName,
  setPicture,
  setEmail,
  setEmail_verified,
  setSideMenuSlider,
} from "../../redux/features/auth-slice";
import { getAllUserProfiles } from "../../redux/features/user-slice";
import { clearSchedulesFilterValues } from "../../redux/features/schedules-main/schedules-filter-slice";

// new navigation helpers
import {
  Button,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popper,
  Paper,
  MenuList,
  ClickAwayListener,
  Grow,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { AppUrls } from "../../AppUrls";

// icons
import {
  MapOutlined,
  PeopleOutline,
  GroupsOutlined,
  DashboardOutlined,
  DescriptionOutlined,
  FactCheckOutlined,
  WorkspacePremiumOutlined,
  PolicyOutlined,
  CalendarMonthOutlined,
  TravelExploreOutlined,
  EventAvailableOutlined,
  AssignmentTurnedInOutlined,
  SettingsOutlined,
  BusinessOutlined,
} from "@mui/icons-material";

import ABAEngineLogo from "../../assets/images/logos/abaengine.png";

import "./header.scss";
import { setSelectedBoardTab } from "../../redux/features/custom-board/custom-board-slice";
import {
  getTenantPlan,
  getTenantUsage,
} from "src/redux/features/plan-billing/plan-billing-slice";
import { resetAllEmails } from "src/redux/features/mailBox/mailbox-slice";
import { OrgService } from "src/constants/company-user";
import Message from "../../assets/images/images-png/Message.png";
import MailBox from "../../assets/images/images-png/MailBox.png";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: "100%",
  },
}));

export const Header: React.FC = () => {
  // #region variables region

  //new selected tab on re-render
  const [newSelectedTab] = useState<string>("");

  //used to track location
  const location = useLocation();

  //ref for newSelectedTab
  const activeTabRef = useRef(newSelectedTab);

  //search value
  const searchValue = useAppSelector((state) => state.activeStep.searchValue);

  //side menu slide controller
  const sideMenuSlider = useAppSelector((state) => state.auth.sideMenuSlider);

  //selected tab
  const [selectedTab, setSelectedTab] = useState<string>("Map");

  //
  const [openSearch, setOpenSearch] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // shared hover menu state
  const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
  const [hoverAnchorEl, setHoverAnchorEl] = useState<HTMLElement | null>(null);
  const hoverLeaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverClose = () => {
    if (hoverLeaveTimeout.current) {
      clearTimeout(hoverLeaveTimeout.current);
      hoverLeaveTimeout.current = null;
    }
  };

  const scheduleHoverClose = () => {
    clearHoverClose();
    hoverLeaveTimeout.current = setTimeout(() => {
      setActiveMenuKey(null);
      setHoverAnchorEl(null);
    }, 150);
  };

  const openHoverMenu = (key: string) => (e: React.MouseEvent<HTMLElement>) => {
    clearHoverClose();
    setActiveMenuKey(key);
    setHoverAnchorEl(e.currentTarget);
  };

  // User menu hover state
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const userLeaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearUserMenuClose = () => {
    if (userLeaveTimeout.current) {
      clearTimeout(userLeaveTimeout.current);
      userLeaveTimeout.current = null;
    }
  };

  const scheduleUserMenuClose = () => {
    clearUserMenuClose();
    userLeaveTimeout.current = setTimeout(() => {
      setUserMenuAnchor(null);
    }, 150);
  };

  const openUserMenu = (e: React.MouseEvent<HTMLElement>) => {
    clearUserMenuClose();
    setUserMenuAnchor(e.currentTarget);
  };

  /** common nav goto helper – keeps existing side-effects */
  const goTo = (url: string, ctx?: "Schedules" | "Board" | "Mailbox") => {
    if (ctx === "Schedules") dispatch(clearSchedulesFilterValues({}));
    if (ctx === "Board")
      dispatch(setSelectedBoardTab({ id: "Intake", type: "Clients" }));
    if (ctx === "Mailbox") dispatch(resetAllEmails());
    dispatch(setSearchValue(""));
    navigate(url);
    if (location.pathname !== "/schedule") {
      localStorage.removeItem("setCounter");
    }
  };

  const { isAuthenticated } = useSession();
  const { user } = useUser();

  const orgLogo = useAppSelector(
    (state) => state.organizationSlice.orgData.orgLogo.url
  );

  const orgService = useAppSelector(
    (state) => state.organizationSlice.orgData.orgService
  );

  const currentTenantId = user?.customAttributes?.currentTenantId;

  const isMentalHealthEnabled = orgService.includes(OrgService.MentalHealth);
  const isAllowedTenantForSessions =
    process.env.REACT_APP_ENVIRONMENT === "DEV" ||
    process.env.REACT_APP_ENVIRONMENT === "QA" ||
    process.env.REACT_APP_ENVIRONMENT === "LOCAL" || //note: localdev, localqa, localprod
    (process.env.REACT_APP_ENVIRONMENT === "PROD" &&
      currentTenantId &&
      (currentTenantId === "T2YQRMXYeyzIFEvqRX0oYTrn0b4B" || //production demo account: Kudo's Center
        currentTenantId === "T2h7DvmKeAaAakqQ9QpQ7Dd3gnAS" || //production demo account: Practice Autism Center
        currentTenantId === "T2eYo974YwmC6wvsQmDm0ZIg2QNa" || //production demo account: Giggles and Gains
        currentTenantId === "T2nGO7oThfhjxculR20zXIBoeO2m")); //production account: Grace Therapy Center

  // Define menu items for each navigation menu
  const contactsMenuItems = [
    {
      text: "Map",
      icon: <MapOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Map)
    },
    {
      text: "Clients",
      icon: <PeopleOutline fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Clients)
    },
    {
      text: "Employees",
      icon: <GroupsOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Employees)
    }
  ];

  const workflowMenuItems = [
    {
      text: "Board",
      icon: <DashboardOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Board, "Board")
    },
    {
      text: "Forms",
      icon: <DescriptionOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Forms)
    },
    ...(isMentalHealthEnabled ? [{
      text: "Assessments",
      icon: <AssignmentTurnedInOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Assessments)
    }] : []),
    {
      text: "Authorizations",
      icon: <FactCheckOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Authorizations)
    },
    {
      text: "Certifications",
      icon: <WorkspacePremiumOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Certifications)
    },
    {
      text: "Insurances",
      icon: <PolicyOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Insurances)
    },
    {
      text: "Paneled With",
      icon: <HealthAndSafetyOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.PaneledWith)
    }
  ];

  const scheduleMenuItems = [
    {
      text: "Planning",
      icon: <CalendarMonthOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Planning)
    },
    {
      text: "Match Finder",
      icon: <TravelExploreOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Schedules)
    }
  ];

  const billingMenuItems = isAllowedTenantForSessions ? [
    {
      text: "Sessions",
      icon: <EventAvailableOutlined fontSize="small" />,
      onClick: () => goTo(AppUrls.Client.Session, "Schedules")
    }
  ] : [];

  // We need this in case user goes to message templates without visiting user management page, or tenantUsers will be empty array.
  useEffect(() => {
    if (user) {
      dispatch(
        getAllUserProfiles({
          authTenantId: user?.customAttributes?.currentTenantId,
          callerEmail: user?.email as string,
          callerName: user?.name as string,
        })
      );
      dispatch(getTenantPlan());
      dispatch(getTenantUsage());
    }
  }, [user, dispatch]);

  //----------------------------------------------------------------
  //Sync Current User in Auth Slice
  useEffect(() => {
    async function fetchData() {
      if (user && isAuthenticated) {
        //dispatch(setNickname(user?.nickname));
        dispatch(setName(user?.name));
        dispatch(setPicture(user?.picture));
        dispatch(setEmail(user?.email));
        dispatch(setEmail_verified(user?.verifiedEmail));
        //dispatch(setSub(user?.sub));
        //dispatch(setUpdated_at(user?.updated_at));
      }
    }
    fetchData();
  }, [user, dispatch, isAuthenticated]);

  const setUpdatedActiveTab = useCallback(() => {
    if (activeTabRef.current) {
      if (
        selectedTab.toLowerCase().includes(activeTabRef.current.toLowerCase())
      ) {
        setSelectedTab(selectedTab);
      } else {
        setSelectedTab(activeTabRef.current);
      }
    }
  }, [selectedTab]);

  useEffect(() => {
    let newTab = "";
    let count = 0;
    let i = 1;

    while (count < 1) {
      if (location.pathname[i] !== "/" && location.pathname[i]) {
        newTab += location.pathname[i];
        i++;
      } else {
        count = 1;
      }
    }

    if (newTab !== activeTabRef.current) {
      if (newTab === "map") {
        activeTabRef.current = "Map";
      } else if (newTab === "planning") {
        activeTabRef.current = "Planning";
      } else if (newTab === "assessment") {
        activeTabRef.current = "Assessments";
      } else if (newTab === "message-center") {
        activeTabRef.current = "Message";
      } else {
        activeTabRef.current = newTab;
      }
    }
    setUpdatedActiveTab();
  }, [location.pathname, setUpdatedActiveTab]);

  const { logout } = useDescope();

  const handleUserLogout = useCallback(() => {
    localStorage.removeItem("selectedTab");
    localStorage.removeItem("setCounter");
    localStorage.removeItem("selectedIntegrationTab");
    localStorage.removeItem("path");
    logout();
    // dispatch(resetCurrentTenant(""));
    // navigate("/sign-in");
  }, [logout]);

  const handleSearchChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    dispatch(setSearchValue(e.target.value));
  };

  return (
    <Box>
      <AppBar position="relative">
        <Toolbar
          className="mainHeader"
          style={{
            color: "black",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box className="mainHeader_left">
            <IconButton
              className="mainHeader__toggle"
              size="large"
              edge="start"
              sx={{ mr: 2 }}
              style={{ background: "transform", color: "#000" }}
              onClick={() => dispatch(setSideMenuSlider(!sideMenuSlider))}
            >
              <Menu />
            </IconButton>
            {/*TODO: later need to read logo file from Org.Files */}
            <img
              alt="Loading"
              src={orgLogo ? orgLogo : ABAEngineLogo}
              className="mainHeader__logo"
            />
            <Search
              className={
                openSearch === true
                  ? "search-block active-search"
                  : "search-block"
              }
            >
              <SearchIconWrapper
                className="search-block__icon"
                // onClick={() => handleSearch()}
              >
                <SearchIcon color="primary" />
              </SearchIconWrapper>
              <StyledInputBase
                onChange={(
                  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => handleSearchChange(e)}
                value={searchValue}
                style={{ color: "black" }}
                placeholder="Search name"
              />
            </Search>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <Box
              className="mobileSearch"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenSearch(!openSearch)}
            >
              <SearchIcon color="primary" />
            </Box>

            <div className="nav-header">
              <Box display="flex" gap={2}>
                {/* Top navigation buttons */}
                <Box
                  display="flex"
                  gap={2}
                  onMouseLeave={scheduleHoverClose}
                  onMouseEnter={clearHoverClose}
                >
                  <Button
                    color="inherit"
                    endIcon={<KeyboardArrowDown />}
                    onMouseEnter={openHoverMenu("Contacts")}
                    onClick={openHoverMenu("Contacts")}
                  >
                    <span className="menuItem__link">Contacts</span>
                  </Button>

                  <Button
                    color="inherit"
                    endIcon={<KeyboardArrowDown />}
                    onMouseEnter={openHoverMenu("Workflow")}
                    onClick={openHoverMenu("Workflow")}
                  >
                    <span className="menuItem__link">Workflow</span>
                  </Button>

                  <Button
                    color="inherit"
                    endIcon={<KeyboardArrowDown />}
                    onMouseEnter={openHoverMenu("Schedule")}
                    onClick={openHoverMenu("Schedule")}
                  >
                    <span className="menuItem__link">Schedule</span>
                  </Button>

                  {billingMenuItems.length > 0 && (
                    <Button
                      color="inherit"
                      endIcon={<KeyboardArrowDown />}
                      onMouseEnter={openHoverMenu("Billing")}
                      onClick={openHoverMenu("Billing")}
                    >
                      <span className="menuItem__link">Billing</span>
                    </Button>
                  )}
                </Box>

                {/* Shared Popper for hover menus */}
                <Popper
                  open={Boolean(activeMenuKey && hoverAnchorEl)}
                  anchorEl={hoverAnchorEl}
                  placement="bottom-start"
                  transition
                  modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
                  style={{ zIndex: 1302 }}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
                      <Paper
                        onMouseEnter={clearHoverClose}
                        onMouseLeave={scheduleHoverClose}
                        elevation={3}
                      >
                        <ClickAwayListener
                          onClickAway={() => {
                            setActiveMenuKey(null);
                            setHoverAnchorEl(null);
                          }}
                        >
                          <MenuList dense disablePadding>
                            {(activeMenuKey === "Contacts"
                              ? contactsMenuItems
                              : activeMenuKey === "Workflow"
                              ? workflowMenuItems
                              : activeMenuKey === "Schedule"
                              ? scheduleMenuItems
                              : activeMenuKey === "Billing"
                              ? billingMenuItems
                              : []
                            ).map((item, idx) => (
                              <MenuItem
                                key={idx}
                                onClick={() => {
                                  setActiveMenuKey(null);
                                  setHoverAnchorEl(null);
                                  item.onClick();
                                }}
                              >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText>{item.text}</ListItemText>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>

                {/* <div>
                  <Button
                    disabled
                    sx={{
                      color: "grey.500", // text color
                      opacity: 0.6, // faded look
                      cursor: "not-allowed",
                    }}
                  >
                    <span className="menuItem__link">Accounting</span>
                  </Button>
                </div> */}
              </Box>
            </div>

            <Box>
              {isAuthenticated && (
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  className="mainHeader__menu desk-menu"
                >
                  {/* Mail & Chat quick-access buttons replaced with images */}
                  <div
                    onClick={() => goTo(AppUrls.Client.MailBox, "Mailbox")}
                    className="menuItem__link"
                  >
                    <img
                      src={MailBox}
                      alt="Mailbox icon"
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <div
                    onClick={() => goTo(AppUrls.Client.Message)}
                    className="menuItem__link"
                  >
                    <img
                      src={Message}
                      alt="Message icon"
                      style={{ cursor: "pointer" }}
                    />
                  </div>

                  <Box
                    sx={{ flexGrow: 0 }}
                    className="userBox"
                    alignItems={"center"}
                    onMouseEnter={openUserMenu}
                    onMouseLeave={scheduleUserMenuClose}
                  >
                    <Tooltip title="" className="userBox__image">
                      <Avatar
                        alt="userProfile"
                        src={user?.picture}
                      />
                    </Tooltip>
                    <Typography>
                      {user?.name?.split(" ")[0]
                        ? user?.name?.split(" ")[0]
                        : ""}
                    </Typography>
                  </Box>
                  
                  {/* User menu Popper */}
                  <Popper
                    open={Boolean(userMenuAnchor)}
                    anchorEl={userMenuAnchor}
                    placement="bottom-end"
                    transition
                    modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
                    style={{ zIndex: 1302 }}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                        <Paper
                          onMouseEnter={clearUserMenuClose}
                          onMouseLeave={scheduleUserMenuClose}
                          elevation={3}
                        >
                          <ClickAwayListener
                            onClickAway={() => {
                              setUserMenuAnchor(null);
                            }}
                          >
                            <MenuList dense disablePadding>
                              <MenuItem
                                onClick={() => {
                                  setUserMenuAnchor(null);
                                  goTo(AppUrls.Client.BusinessSettings);
                                }}
                              >
                                <ListItemIcon>
                                  <SettingsOutlined fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Settings</ListItemText>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setUserMenuAnchor(null);
                                  goTo(AppUrls.Client.CompanyProfile);
                                }}
                              >
                                <ListItemIcon>
                                  <BusinessOutlined fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Account</ListItemText>
                              </MenuItem>
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                  
                  <Logout onClick={handleUserLogout} className="logout-icon" />
                </Box>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Header;
